import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import ChairIcon from "@mui/icons-material/Chair";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const API = process.env.REACT_APP_API_URL;

const LibraryBookingForm = ({ seatId, open, onClose, onSuccess }) => {
  const [seat, setSeat] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [purposeOfJoining, setPurposeOfJoining] = useState("");
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [studentPhoto, setStudentPhoto] = useState(null);

  // ✅ NEW: Optional ID Card Photo
  const [idCardPhoto, setIdCardPhoto] = useState(null);

  const [hasProfilePhoto, setHasProfilePhoto] = useState(true);
  const [student, setStudent] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [errors, setErrors] = useState({});

  // 🔁 New: global billing settings from backend
  const [billingMode, setBillingMode] = useState(null); // "fixed_tenure" | "rolling_monthly" | "calendar_monthly"
  const [billingSettings, setBillingSettings] = useState(null);

  const [billingCycle, setBillingCycle] = useState(3); // booking cycle in months (3/6/9/12 OR default from settings)
  const [endDate, setEndDate] = useState(""); // auto-calculated end date

  // Helper: calculate end date from start date + cycle + billing mode
  const calculateEndDate = (start, months, mode) => {
    if (!start) return "";
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) return "";

    // 1️⃣ Calendar Monthly → first period = month ke last din tak
    if (mode === "calendar_monthly") {
      // JS trick: new Date(year, monthIndex+1, 0) => last day of current month
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      return end.toISOString().split("T")[0];
    }

    // 2️⃣ Rolling / Fixed → start + months - 1 day
    const m = Number(months) || 1;
    const end = new Date(d);
    end.setMonth(end.getMonth() + m);
    end.setDate(end.getDate() - 1);
    return end.toISOString().split("T")[0]; // yyyy-mm-dd
  };

  // 🔁 Fetch seat, profile & billing settings
  useEffect(() => {
    if (!seatId) return;

    const fetchData = async () => {
      const loadingToast = toast.loading("Loading...");
      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        // 1) Seat, student profile, billing settings
        const [seatRes, profileRes, settingsRes] = await Promise.all([
          axios.get(`${API}library/seats/${seatId}/`, config),
          axios.get(`${API}users/me/`, config),
          axios.get(`${API}library/billing-settings/`, config),
        ]);

        const settings = settingsRes.data || {};
        setBillingSettings(settings);
        setBillingMode(settings.mode || null);

        // Decide initial cycle based on mode
        const initialCycle =
          settings.mode === "fixed_tenure"
            ? settings.default_cycle_months || 3
            : settings.default_cycle_months || 1;

        setSeat(seatRes.data);
        setStudent(profileRes.data);
        setHasProfilePhoto(!!profileRes.data.profile_photo);
        setBillingCycle(initialCycle);

        // Billing summary yaha nahi, startDate select hone ke baad calculate hoga
        setBillingSummary(null);

        toast.dismiss(loadingToast);
      } catch (err) {
        toast.dismiss(loadingToast);
        toast.error("❌ Failed to fetch seat or profile.");
        onClose();
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatId]);

  // 🔁 Re-fetch billing summary when cycle OR startDate changes
  useEffect(() => {
    const fetchBillingSummary = async () => {
      if (!seatId || !open) return;
      if (!billingMode) return;
      if (!startDate) return; // slab ke liye start date zaroori hai

      try {
        const token = localStorage.getItem("token");
        const config = { headers: { Authorization: `Bearer ${token}` } };

        const cycleToUse =
          billingMode === "fixed_tenure"
            ? billingCycle
            : billingSettings?.default_cycle_months || 1;

        const params = new URLSearchParams({
          seat_id: seatId,
          cycle_months: String(cycleToUse),
          start_date: startDate, // ✅ slab-based fee ke liye
        });

        const res = await axios.get(
          `${API}library/billing-summary/?${params.toString()}`,
          config
        );
        setBillingSummary(res.data);
      } catch (err) {
        toast.error("❌ Failed to update billing summary.");
      }
    };

    fetchBillingSummary();
  }, [billingCycle, seatId, open, billingMode, billingSettings, startDate]);

  // Auto-update end date when startDate or billing settings change
  useEffect(() => {
    if (!startDate) {
      setEndDate("");
      return;
    }

    const cycleForEndDate =
      billingMode === "fixed_tenure"
        ? billingCycle
        : billingSettings?.default_cycle_months || 1;

    const result = calculateEndDate(startDate, cycleForEndDate, billingMode);
    setEndDate(result);
  }, [startDate, billingCycle, billingMode, billingSettings]);

  // Reset form when opened
  useEffect(() => {
    if (open) {
      setStartDate("");
      setPurposeOfJoining("");
      setAadhaarFront(null);
      setAadhaarBack(null);
      setStudentPhoto(null);

      // ✅ NEW: reset id card
      setIdCardPhoto(null);

      setErrors({});
      setBillingSummary(null);
      setEndDate("");

      // Reset cycle based on current settings instead of hard-coded 3
      const defaultCycle =
        billingMode === "fixed_tenure"
          ? billingSettings?.default_cycle_months || 3
          : billingSettings?.default_cycle_months || 1;

      setBillingCycle(defaultCycle);
    }
  }, [open, billingMode, billingSettings]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};

    // Validate required fields
    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!purposeOfJoining.trim()) {
      newErrors.purposeOfJoining = "Purpose of joining is required";
    }

    // Only require Aadhaar photos if student doesn't have them
    if (!student?.aadhaar_front_photo && !aadhaarFront) {
      newErrors.aadhaarFront = "Aadhaar front photo is required";
    }

    if (!student?.aadhaar_back_photo && !aadhaarBack) {
      newErrors.aadhaarBack = "Aadhaar back photo is required";
    }

    if (!hasProfilePhoto && !studentPhoto) {
      newErrors.studentPhoto = "Profile photo is required";
    }

    // ✅ ID card is OPTIONAL → no validation

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setConfirmOpen(true);
  };

  const submitFinalBooking = async () => {
    const token = localStorage.getItem("token");
    const loadingToast = toast.loading("Loading...");
    try {
      // Update profile photo if needed
      if (!hasProfilePhoto && studentPhoto) {
        const photoForm = new FormData();
        photoForm.append("profile_photo", studentPhoto);
        await axios.patch(`${API}users/update-profile-photo/`, photoForm, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      const formData = new FormData();
      formData.append("seat_id", seatId);
      formData.append("start_date", startDate);
      formData.append("purpose_of_joining", purposeOfJoining);

      // 🔁 Choose correct cycle to send to backend
      const cycleToSend =
        billingMode === "fixed_tenure"
          ? billingCycle
          : billingSettings?.default_cycle_months || 1;

      formData.append("billing_cycle_months", cycleToSend);

      // Send calculated end date (for UI consistency)
      if (endDate) {
        formData.append("end_date", endDate);
      }

      // Only append Aadhaar photos if they are provided
      if (aadhaarFront) {
        formData.append("aadhaar_front_photo", aadhaarFront);
      }
      if (aadhaarBack) {
        formData.append("aadhaar_back_photo", aadhaarBack);
      }

      // ✅ NEW: Optional ID card photo
      if (idCardPhoto) {
        formData.append("id_card_photo", idCardPhoto);
      }

      await axios.post(`${API}library/bookings/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.dismiss(loadingToast);
      toast.success("✅ Booking request sent!");
      setConfirmOpen(false);
      onSuccess();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      const msg =
        err.response?.data?.error || "❌ Booking failed. Check your input.";
      toast.error(msg);
      setConfirmOpen(false);
    }
  };

  const bookingDetails = [
    { label: "Monthly Fee", value: `₹${billingSummary?.monthly_fee || 0}` },
    { label: "Deposit", value: `₹${billingSummary?.deposit_amount || 0}` },
    { label: "Total", value: `₹${billingSummary?.total || 0}` },
  ];

  const FileUploadButton = ({
    label,
    onChange,
    required = false,
    accept = "image/png,image/jpg,image/jpeg",
    value = null,
    error = null,
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={onChange}
          className="hidden"
          id={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          name={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
        />
        <label
          htmlFor={`file-${label.replace(/\s+/g, "-").toLowerCase()}`}
          className={`flex flex-col items-center justify-center gap-1 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
            error
              ? "border-red-500 bg-red-50 text-red-700"
              : value
              ? "border-green-500 bg-green-50 text-green-700"
              : "border-gray-300 text-gray-600 hover:border-blue-400"
          }`}
        >
          {value ? (
            <>
              <CheckCircleIcon className="text-green-600" />
              <span className="font-medium">{value.name}</span>
            </>
          ) : (
            <>
              <CloudUploadIcon />
              <span className="font-medium">Click to upload</span>
              <span className="text-xs text-gray-500">
                Max file size: 2MB | Accepted: PNG, JPG, JPEG
              </span>
            </>
          )}
        </label>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    </div>
  );

  // Helper for billing mode label
  const getBillingModeLabel = () => {
    if (!billingMode) return "";
    const months = billingSettings?.default_cycle_months || 1;
    const monthText = months > 1 ? `${months} Months` : `${months} Month`;

    if (billingMode === "fixed_tenure") return `${monthText} (Fixed Tenure)`;
    if (billingMode === "rolling_monthly") return `${monthText} (Rolling Monthly)`;
    if (billingMode === "calendar_monthly") return `${monthText} (Calendar Monthly)`;
    return monthText;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6">
        <DialogTitle className="flex items-center gap-3 text-white font-bold text-xl">
          <ChairIcon className="text-2xl" />
          Book Library Seat
        </DialogTitle>
      </div>

      <DialogContent className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
        {seat && (
          <form
            id="library-booking-form"
            onSubmit={handleSubmit}
            className="space-y-6"
            noValidate
          >
            {/* Seat Information Card */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
              <Typography className="text-lg font-semibold text-gray-800 mb-2">
                Seat Information
              </Typography>
              <Typography className="text-gray-600">
                Selected Seat:{" "}
                <span className="font-semibold text-blue-600">
                  {seat.seat_number}
                </span>
              </Typography>
            </div>

            {/* Booking Details Card */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <Typography className="text-lg font-semibold text-gray-800 mb-4">
                Booking Details
              </Typography>

              <div className="space-y-4 mt-4">
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (errors.startDate) {
                      setErrors((prev) => ({ ...prev, startDate: null }));
                    }
                  }}
                  inputProps={{
                    min: new Date().toISOString().split("T")[0],
                  }}
                  className="mb-4"
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />

                {/* 🔁 Billing cycle UI controlled by global settings */}
                {billingMode === "fixed_tenure" ? (
                  <>
                    {/* Booking Cycle (3, 6, 9, 12 months) */}
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Booking Duration (Months)"
                      fullWidth
                      value={billingCycle}
                      onChange={(e) => setBillingCycle(parseInt(e.target.value, 10))}
                      className="mb-4"
                    >
                      <option value={1}>1 Month(Only for 11th and 12th Students)</option>
                      <option value={2}>2 Months(Only for 11th and 12th Students)</option>
                      <option value={3}>3 Months</option>
                      <option value={6}>6 Months</option>
                      <option value={9}>9 Months</option>
                      <option value={12}>12 Months (1 Year)</option>

                    </TextField>

                    {/* Auto-calculated End Date (read-only) */}
                    <TextField
                      label="End Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={endDate}
                      disabled
                      helperText={
                        startDate
                          ? "Auto-calculated from start date & booking duration"
                          : "Select start date to see end date"
                      }
                      className="mb-4"
                    />
                  </>
                ) : (
                  <>
                    {/* Read-only billing cycle info for rolling/calendar modes */}
                    <TextField
                      label="Billing Cycle"
                      fullWidth
                      value={getBillingModeLabel()}
                      InputProps={{ readOnly: true }}
                      className="mb-4"
                    />

                    {/* End date shown as information based on default cycle */}
                    <TextField
                      label="End Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={endDate}
                      disabled
                      helperText={
                        startDate
                          ? "Auto-calculated from start date & billing cycle"
                          : "Select start date to see end date"
                      }
                      className="mb-4"
                    />
                  </>
                )}

                <TextField
                  fullWidth
                  type="text"
                  label="Purpose of joining"
                  value={purposeOfJoining}
                  onChange={(e) => {
                    setPurposeOfJoining(e.target.value);
                    if (errors.purposeOfJoining) {
                      setErrors((prev) => ({
                        ...prev,
                        purposeOfJoining: null,
                      }));
                    }
                  }}
                  required
                  multiline
                  rows={3}
                  className="mb-4"
                  error={!!errors.purposeOfJoining}
                  helperText={errors.purposeOfJoining}
                />
              </div>
            </div>

            {/* Document Upload Card */}
            {(!student?.aadhaar_front_photo ||
              !student?.aadhaar_back_photo ||
              !hasProfilePhoto) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <Typography className="text-lg font-semibold text-gray-800 mb-4">
                  Required Documents
                </Typography>

                <div className="space-y-4 mt-4">
                  {!student?.aadhaar_front_photo && (
                    <FileUploadButton
                      label="Aadhaar Front Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("File must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setAadhaarFront(file);
                          if (errors.aadhaarFront) {
                            setErrors((prev) => ({
                              ...prev,
                              aadhaarFront: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={aadhaarFront}
                      error={errors.aadhaarFront}
                    />
                  )}

                  {!student?.aadhaar_back_photo && (
                    <FileUploadButton
                      label="Aadhaar Back Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("File must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setAadhaarBack(file);
                          if (errors.aadhaarBack) {
                            setErrors((prev) => ({
                              ...prev,
                              aadhaarBack: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={aadhaarBack}
                      error={errors.aadhaarBack}
                    />
                  )}

                  {!hasProfilePhoto && (
                    <FileUploadButton
                      label="Profile Photo"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        const maxSize = 2 * 1024 * 1024;
                        if (file && file.size > maxSize) {
                          toast.error("Profile photo must be less than 2MB");
                          e.target.value = null;
                        } else {
                          setStudentPhoto(file);
                          if (errors.studentPhoto) {
                            setErrors((prev) => ({
                              ...prev,
                              studentPhoto: null,
                            }));
                          }
                        }
                      }}
                      required
                      value={studentPhoto}
                      error={errors.studentPhoto}
                    />
                  )}
                </div>
              </div>
            )}

            {/* ✅ NEW: Optional ID Card Upload Card (always visible) */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <Typography className="text-lg font-semibold text-gray-800 mb-4">
              </Typography>

              <div className="space-y-4 mt-4">
                <FileUploadButton
                  label="ID Card Photo (Optional)"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    const maxSize = 2 * 1024 * 1024;
                    if (file && file.size > maxSize) {
                      toast.error("ID card photo must be less than 2MB");
                      e.target.value = null;
                    } else {
                      setIdCardPhoto(file);
                    }
                  }}
                  required={false}
                  value={idCardPhoto}
                  error={null}
                />
              </div>
            </div>

            {/* Billing Summary Card */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
              <Typography className="text-lg font-semibold text-gray-800 mb-4">
                Billing Summary
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {bookingDetails.map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="text-sm text-gray-600 mb-1">
                      {item.label}
                    </div>
                    <div className="text-xl font-bold text-gray-800">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </form>
        )}
      </DialogContent>

      <DialogActions className="justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
        <Button onClick={onClose} variant="outlined" className="px-6 py-2">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          form="library-booking-form"
          className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          Submit Booking
        </Button>
      </DialogActions>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <DialogTitle className="text-white font-bold text-xl">
            Confirm Booking
          </DialogTitle>
        </div>
        <DialogContent className="p-6 space-y-3">
          <div className="bg-gray-50 p-4 rounded-lg">
            <Typography className="font-semibold text-gray-800 mb-3">
              Bussiness Track Hostel & Library
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium">
                  {student.first_name} {student.last_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Phone:</span>
                <span className="font-medium">{student.phone_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Email:</span>
                <span className="font-medium">{student.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Address:</span>
                <span className="font-medium">{student.address}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Seat:</span>
                <span className="font-medium">{seat?.seat_number}</span>
              </div>

              {/* ✅ NEW: ID Card status (small, same layout) */}
              <div className="flex justify-between">
                <span className="text-gray-600">ID Card:</span>
                <span className="font-medium">
                  {idCardPhoto ? "Uploaded" : "Not provided"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <Typography className="font-semibold text-gray-800 mb-2">
              Payment Summary
            </Typography>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Fee:</span>
                <span className="font-medium">
                  ₹{billingSummary?.monthly_fee || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration:</span>
                <span className="font-medium">
                  {billingMode === "fixed_tenure"
                    ? `${billingSummary?.cycle_months || billingCycle} Months`
                    : getBillingModeLabel()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Deposit:</span>
                <span className="font-medium">
                  ₹{billingSummary?.deposit_amount || 0}
                </span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-800 font-semibold">Total:</span>
                <span className="text-lg font-bold text-green-600">
                  ₹{billingSummary?.total || 0}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions className="p-6">
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            className="px-6 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={submitFinalBooking}
            variant="contained"
            color="primary"
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
          >
            Confirm & Book
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LibraryBookingForm;
